import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Cpu, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function RequestDemo() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "", audience: "business", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/demo-requests", form); setDone(true); }
    catch { toast.error("Could not submit. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <CheckCircle2 className="w-16 h-16 text-teal-600 mx-auto mb-5" />
      <h1 className="font-display text-3xl font-bold text-blue-950">Thanks — request received!</h1>
      <p className="mt-3 text-slate-600">Our team will reach out to schedule your demo and discuss your goals.</p>
      <Button onClick={() => navigate("/")} className="mt-7 bg-blue-950 text-white h-12 px-7">Back to home</Button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-8">
          <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-blue-950 text-teal-400 mx-auto mb-4"><Cpu className="w-7 h-7" /></span>
          <h1 className="font-display text-4xl font-bold text-blue-950">Request a Demo</h1>
          <p className="mt-2 text-slate-600">See how T5 Workforce, Academy and Intelligence can support your organization.</p>
        </div>
        <form onSubmit={submit} data-testid="demo-form" className="bg-white rounded-2xl p-8 shadow-lg border border-blue-900/5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input required data-testid="demo-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Work email</Label><Input type="email" required data-testid="demo-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Organization</Label><Input data-testid="demo-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="demo-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>I represent</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="demo-audience" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="business">A business / care organization</SelectItem>
                <SelectItem value="government">A government / public-sector agency</SelectItem>
                <SelectItem value="organization">A community / nonprofit organization</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>What are you looking to solve? (optional)</Label><Textarea rows={4} data-testid="demo-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" disabled={loading} data-testid="demo-submit" className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request a Demo"}</Button>
        </form>
      </motion.div>
    </div>
  );
}
